import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTrainer, bookSession } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { 
  Shield, Award, Star, MapPin, Clock, Calendar as CalendarIcon, 
  CheckCircle, ArrowLeft, DollarSign, ShieldCheck,
  Phone, Mail, Instagram, Youtube, Facebook, Music2,
  Share2, Check as CheckIcon
} from 'lucide-react';
import { getInitials, CATEGORY_NAMES, formatCurrency, getTrainerPriceForCategory, waiverOk } from '../lib/utils';
import { toast } from 'sonner';
import { format } from 'date-fns';
import ReviewsSection from '../components/ReviewsSection';
import LiabilityWaiverModal from '../components/LiabilityWaiverModal';
import TrainerPackagesPublic from '../components/TrainerPackagesPublic';
import { TrainerCurriculumDisplay, TrainerGroupEventsDisplay } from '../components/TrainerCurriculumAndEvents';

const VERIFICATION_CONFIG = {
  verified: { icon: Shield, label: 'Professional', className: 'badge-verified' },
  elite: { icon: Award, label: 'Elite Coach', className: 'badge-elite' },
  specialist: { icon: Star, label: 'Specialist', className: 'badge-specialist' },
};

const ShareProfileButton = ({ trainerName }) => {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = window.location.href;
    const shareData = {
      title: trainerName ? `${trainerName} on DojoDash` : 'DojoDash',
      text: trainerName
        ? `Book ${trainerName} on DojoDash, at-home personal training, self-defense and Jiu-Jitsu.`
        : 'Find at-home coaches on DojoDash.',
      url,
    };

    // Prefer native share sheet on mobile / supported browsers
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (err) {
        // User cancelled, fall through to copy
        if (err?.name === 'AbortError') return;
      }
    }

    // Fallback: write to clipboard via execCommand (works in older browsers /
    // headless test envs that don't expose navigator.clipboard).
    const fallbackCopy = (text) => {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      let ok = false;
      try {
        ok = document.execCommand('copy');
      } catch {
        ok = false;
      }
      document.body.removeChild(ta);
      return ok;
    };

    let copiedOk = false;
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(url);
        copiedOk = true;
      } catch {
        copiedOk = fallbackCopy(url);
      }
    } else {
      copiedOk = fallbackCopy(url);
    }

    if (copiedOk) {
      setCopied(true);
      toast.success('Profile link copied');
      setTimeout(() => setCopied(false), 1500);
    } else {
      toast.error('Could not copy, copy this manually: ' + url);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleShare}
      className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white"
      data-testid="share-profile-btn"
    >
      {copied ? (
        <>
          <CheckIcon className="w-4 h-4 mr-2 text-emerald-400" />
          Copied
        </>
      ) : (
        <>
          <Share2 className="w-4 h-4 mr-2" />
          Share
        </>
      )}
    </Button>
  );
};

const CATEGORY_OPTIONS = [
  { value: 'personal_training', label: 'Personal Training' },
  { value: 'self_defense', label: 'Self-Defense' },
  { value: 'jiujitsu', label: 'Jiu-Jitsu' },
];

const TrainerProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, isClient, user } = useAuth();
  const [trainer, setTrainer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [bookingData, setBookingData] = useState({
    category: '',
    scheduled_date: null,
    scheduled_time: '',
    location_address: '',
    notes: '',
  });

  const loadTrainer = useCallback(async () => {
    try {
      const response = await getTrainer(id);
      setTrainer(response.data);
    } catch (error) {
      toast.error('Trainer not found');
      navigate('/trainers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadTrainer();
  }, [loadTrainer]);

  const profilePartial = trainer?.trainer_profile || {};
  const trainerPaymentsReady = !!profilePartial.stripe_charges_enabled;

  const selectedPrice = useMemo(() => {
    if (!bookingData.category || !trainer) return null;
    return getTrainerPriceForCategory(trainer.trainer_profile, bookingData.category);
  }, [bookingData.category, trainer]);

  const handleBookSession = async () => {
    if (!bookingData.category || !bookingData.scheduled_date || !bookingData.scheduled_time || !bookingData.location_address) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!trainerPaymentsReady) {
      toast.error('This trainer is not yet accepting payments.');
      return;
    }
    if (!selectedPrice || selectedPrice <= 0) {
      toast.error('This trainer hasn\u2019t set a price for that service yet.');
      return;
    }

    // First-booking liability waiver gate
    const waiverAccepted = waiverOk(user);
    if (!waiverAccepted) {
      setBookingOpen(false);
      setWaiverOpen(true);
      return;
    }

    proceedToCheckout();
  };

  const proceedToCheckout = async () => {
    setBookingLoading(true);
    try {
      // Create the booking session first so /checkout has a session_id
      // it can hand to Stripe hosted checkout.
      const res = await bookSession({
        trainer_id: trainer.id,
        category: bookingData.category,
        scheduled_date: format(bookingData.scheduled_date, 'yyyy-MM-dd'),
        scheduled_time: bookingData.scheduled_time,
        duration_minutes: 60,
        location_address: bookingData.location_address,
        notes: bookingData.notes,
      });
      const session = res?.data?.session;
      if (!session?.id) {
        throw new Error('Failed to create booking session');
      }
      setBookingOpen(false);
      navigate('/checkout', {
        state: {
          bookingSessionId: session.id,
          trainerId: trainer.id,
          trainerName: trainer.full_name,
          trainerImage: profilePartial.profile_image,
          category: bookingData.category,
          categoryLabel: CATEGORY_OPTIONS.find((o) => o.value === bookingData.category)?.label || bookingData.category,
          price: session.price,
          amount: session.price,
          platform_fee: session.platform_fee,
          scheduled_date: session.scheduled_date,
          scheduled_time: session.scheduled_time,
          duration_minutes: session.duration_minutes,
          location_address: session.location_address,
          notes: session.notes,
        },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start checkout');
    } finally {
      setBookingLoading(false);
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${i < Math.round(rating) ? 'star-filled fill-current' : 'star-empty'}`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!trainer) return null;

  const profile = trainer.trainer_profile || {};
  const verification = VERIFICATION_CONFIG[profile.verification_level] || VERIFICATION_CONFIG.verified;
  const VerificationIcon = verification.icon;

  const timeSlots = [
    '09:00', '10:00', '11:00', '12:00', '13:00', 
    '14:00', '15:00', '16:00', '17:00', '18:00', '19:00'
  ];

  return (
    <div className="min-h-screen bg-[#09090B] py-8 px-4 sm:px-6 lg:px-8" data-testid="trainer-profile">
      <div className="max-w-5xl mx-auto">
        {/* Back + Share row */}
        <div className="mb-6 flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            onClick={() => navigate('/trainers')}
            className="text-zinc-400 hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Trainers
          </Button>
          <ShareProfileButton trainerName={trainer.full_name} />
        </div>

        {/* Profile Header */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-8 mb-8" data-testid="profile-header">
          <div className="flex flex-col md:flex-row gap-6">
            <Avatar className="w-32 h-32 border-4 border-[#C0A062]/30">
              <AvatarImage src={profile.profile_image} />
              <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-4xl font-bold">
                {getInitials(trainer.full_name)}
              </AvatarFallback>
            </Avatar>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <h1 className="text-3xl font-bold text-white">{trainer.full_name}</h1>
                <Badge className={`${verification.className} flex items-center gap-1 text-sm`}>
                  <VerificationIcon className="w-4 h-4" />
                  {verification.label}
                </Badge>
              </div>

              {(trainer.city || profile.service_area) && (
                <div className="flex items-center gap-2 text-zinc-400 mb-4">
                  <MapPin className="w-4 h-4" />
                  {profile.service_area || trainer.city}
                </div>
              )}

              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center gap-1">
                  {renderStars(trainer.avg_rating || 0)}
                </div>
                <span className="text-zinc-400">
                  {trainer.avg_rating?.toFixed(1) || '0.0'} ({trainer.review_count || 0} reviews)
                </span>
              </div>

              <div className="flex flex-wrap gap-2" data-testid="trainer-specialties">
                {(profile.specializations && profile.specializations.length > 0
                  ? profile.specializations
                  : (profile.categories || []).map((c) => CATEGORY_NAMES[c] || c)
                ).map((label) => (
                  <span
                    key={label}
                    className="text-sm px-3 py-1 bg-zinc-800 text-zinc-300 rounded-full"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>

            <div className="md:text-right">
              {(() => {
                const prices = CATEGORY_OPTIONS
                  .map((o) => getTrainerPriceForCategory(profile, o.value))
                  .filter((p) => p && p > 0);
                const minPrice = prices.length ? Math.min(...prices) : null;
                if (!minPrice) {
                  return (
                    <p className="text-sm text-zinc-500" data-testid="price-tba">Pricing coming soon</p>
                  );
                }
                return (
                  <>
                    <p className="text-sm text-zinc-400 mb-1">Starting from</p>
                    <p className="text-3xl font-bold text-[#C0A062]">{formatCurrency(minPrice)}</p>
                    <p className="text-sm text-zinc-400">per session</p>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* About */}
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="about-section">
              <h2 className="text-xl font-bold text-white mb-4">About</h2>
              <p className="text-zinc-300 whitespace-pre-line">
                {profile.bio || 'No bio provided yet.'}
              </p>
            </div>

            {/* Experience & Certifications */}
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="experience-section">
              <h2 className="text-xl font-bold text-white mb-4">Experience & Certifications</h2>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-[#C0A062]" />
                    <span className="font-medium text-white">Experience</span>
                  </div>
                  <p className="text-zinc-300" data-testid="experience-value">
                    {profile.experience_years == null
                      ? 'Not specified'
                      : Number(profile.experience_years) === 0
                        ? 'Less than a year'
                        : Number(profile.experience_years) === 1
                          ? '1 year'
                          : `${profile.experience_years} years`}
                  </p>
                </div>

                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="w-5 h-5 text-[#C0A062]" />
                    <span className="font-medium text-white">Certifications</span>
                  </div>
                  <div className="space-y-1">
                    {(profile.certifications || []).length > 0 ? (
                      profile.certifications.map((cert, i) => (
                        <div key={`${cert}-${i}`} className="flex items-center gap-2 text-zinc-300" data-testid={`cert-row-${i}`}>
                          <CheckCircle className="w-4 h-4 text-green-400" />
                          {cert}
                        </div>
                      ))
                    ) : (
                      <p className="text-zinc-500">No certifications listed</p>
                    )}
                  </div>
                  {(profile.certifications || []).length > 0 ? (
                    <p className="text-xs text-zinc-500 mt-3" data-testid="certifications-disclaimer">
                      All trainers self-report their credentials. DojoDash does not independently verify
                      certifications or conduct background checks.
                    </p>
                  ) : (
                    <p className="text-xs text-zinc-500 mt-3" data-testid="certifications-disclaimer">
                      None listed. Trainers self-report their credentials, DojoDash does not independently
                      verify certifications or conduct background checks.
                    </p>
                  )}
                </div>
              </div>

              {/* Equipment + Experience levels */}
              {(profile.equipment || (profile.experience_levels || []).length > 0) && (
                <div className="grid sm:grid-cols-2 gap-6 mt-6 pt-6 border-t border-zinc-800" data-testid="equipment-section">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">Equipment</p>
                    <p className="text-zinc-300 text-sm">
                      {profile.equipment === 'full' && 'Brings full equipment'}
                      {profile.equipment === 'some' && 'Brings some equipment'}
                      {(!profile.equipment || profile.equipment === 'none') && 'Client provides equipment'}
                    </p>
                    {profile.equipment_details && (
                      <p className="text-xs text-zinc-500 mt-1.5" data-testid="equipment-details">
                        {profile.equipment_details}
                      </p>
                    )}
                  </div>
                  {(profile.experience_levels || []).length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-zinc-500 font-semibold mb-2">Trains</p>
                      <div className="flex flex-wrap gap-1.5" data-testid="trainer-experience-levels">
                        {(profile.experience_levels || []).map((lvl) => (
                          <span
                            key={lvl}
                            className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-300 capitalize"
                          >
                            {lvl.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Portfolio: photos + video */}
            {((profile.portfolio_photos || []).length > 0 || profile.portfolio_video_url) && (
              <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="portfolio-section">
                <h2 className="text-xl font-bold text-white mb-4">Portfolio</h2>
                {(profile.portfolio_photos || []).length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="portfolio-photos">
                    {(profile.portfolio_photos || []).slice(0, 4).map((photo, i) => (
                      <a
                        key={i}
                        href={photo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="relative aspect-square rounded-md overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-[#C0A062] transition-colors group"
                        data-testid={`portfolio-photo-${i}`}
                      >
                        <img src={photo} alt={`Portfolio ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      </a>
                    ))}
                  </div>
                )}
                {profile.portfolio_video_url && (() => {
                  const url = profile.portfolio_video_url;
                  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
                  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
                  if (ytMatch) {
                    return (
                      <div className="relative aspect-video rounded-md overflow-hidden bg-black" data-testid="portfolio-video-embed">
                        <iframe
                          src={`https://www.youtube.com/embed/${ytMatch[1]}`}
                          title="Coach highlight video"
                          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          className="absolute inset-0 w-full h-full"
                        />
                      </div>
                    );
                  }
                  if (vimeoMatch) {
                    return (
                      <div className="relative aspect-video rounded-md overflow-hidden bg-black" data-testid="portfolio-video-embed">
                        <iframe
                          src={`https://player.vimeo.com/video/${vimeoMatch[1]}`}
                          title="Coach highlight video"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          className="absolute inset-0 w-full h-full"
                        />
                      </div>
                    );
                  }
                  return (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#C0A062] hover:underline text-sm"
                      data-testid="portfolio-video-link"
                    >
                      Watch highlight video →
                    </a>
                  );
                })()}
              </div>
            )}

            {/* Contact & Social */}
            {(profile.phone || profile.contact_email || profile.social_instagram || profile.social_tiktok || profile.social_youtube || profile.social_facebook) && (
              <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="contact-section">
                <h2 className="text-xl font-bold text-white mb-4">Contact &amp; Social</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {profile.phone && (
                    <a href={`tel:${profile.phone}`} className="flex items-center gap-2 text-zinc-300 hover:text-[#C0A062]" data-testid="trainer-phone">
                      <Phone className="w-4 h-4 text-[#C0A062]" />
                      {profile.phone}
                    </a>
                  )}
                  {profile.contact_email && (
                    <a href={`mailto:${profile.contact_email}`} className="flex items-center gap-2 text-zinc-300 hover:text-[#C0A062] break-all" data-testid="trainer-email">
                      <Mail className="w-4 h-4 text-[#C0A062]" />
                      {profile.contact_email}
                    </a>
                  )}
                  {profile.social_instagram && (
                    <a href={profile.social_instagram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-300 hover:text-[#C0A062]">
                      <Instagram className="w-4 h-4 text-[#C0A062]" />
                      Instagram
                    </a>
                  )}
                  {profile.social_tiktok && (
                    <a href={profile.social_tiktok} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-300 hover:text-[#C0A062]">
                      <Music2 className="w-4 h-4 text-[#C0A062]" />
                      TikTok
                    </a>
                  )}
                  {profile.social_youtube && (
                    <a href={profile.social_youtube} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-300 hover:text-[#C0A062]">
                      <Youtube className="w-4 h-4 text-[#C0A062]" />
                      YouTube
                    </a>
                  )}
                  {profile.social_facebook && (
                    <a href={profile.social_facebook} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-zinc-300 hover:text-[#C0A062]">
                      <Facebook className="w-4 h-4 text-[#C0A062]" />
                      Facebook
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Session Packages */}
            <TrainerPackagesPublic trainer={trainer} trainerPaymentsReady={trainerPaymentsReady} />

            {/* Group sessions & seminars */}
            <TrainerGroupEventsDisplay trainerId={trainer.id} />

            {/* Curriculum (default or custom) */}
            <TrainerCurriculumDisplay trainerId={trainer.id} offeredDisciplines={trainer.trainer_profile?.categories} />

            {/* Reviews */}
            <ReviewsSection trainerId={trainer.id} trainerName={trainer.full_name} />
          </div>

          {/* Sidebar - Booking */}
          <div className="space-y-6">
            {/* Trust & Safety panel */}
            <div className="bg-[#18181B] border border-emerald-500/20 rounded-lg p-6" data-testid="trust-panel">
              <h2 className="text-base font-bold text-white mb-4 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                Credentials, In The Open
              </h2>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2 text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                  Coach credentials, certifications, and experience listed publicly
                </li>
                <li className="flex items-center gap-2 text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                  Public reviews and ratings
                </li>
                <li className="flex items-center gap-2 text-zinc-300">
                  <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0" />
                  You choose who trains you
                </li>
              </ul>
            </div>

            <p className="text-xs text-zinc-500 leading-relaxed -mt-2" data-testid="trust-disclaimer">
              All trainers self-report their credentials. DojoDash does not independently verify
              certifications or conduct background checks.
            </p>

            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6 sticky top-24" data-testid="booking-card">
              <h2 className="text-xl font-bold text-white mb-4">Book a Session</h2>

              <div className="space-y-3 mb-6">
                {CATEGORY_OPTIONS.map((opt) => {
                  const price = getTrainerPriceForCategory(profile, opt.value);
                  return (
                    <div key={opt.value} className="flex items-center justify-between p-3 bg-zinc-800/30 rounded-lg">
                      <span className="text-zinc-300">{opt.label}</span>
                      <span className="text-[#C0A062] font-bold">
                        {price ? formatCurrency(price) : <span className="text-zinc-500 text-sm font-normal">Not offered</span>}
                      </span>
                    </div>
                  );
                })}
              </div>

              {!trainerPaymentsReady && (
                <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-200 text-xs" data-testid="trainer-not-payable">
                  Not accepting bookings yet — this coach hasn&apos;t finished connecting their payout account.
                </div>
              )}

              {isAuthenticated && isClient ? (
                <Dialog open={bookingOpen} onOpenChange={setBookingOpen}>
                  <DialogTrigger asChild>
                    <Button
                      className="w-full btn-primary py-6"
                      data-testid="book-now-btn"
                      disabled={!trainerPaymentsReady}
                    >
                      <CalendarIcon className="w-5 h-5 mr-2" />
                      {trainerPaymentsReady ? 'Book Now' : 'Not accepting bookings yet'}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#18181B] border-zinc-800 max-w-lg">
                    <DialogHeader>
                      <DialogTitle className="text-white">Book Session with {trainer.full_name}</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                      <div>
                        <Label className="text-zinc-300">Category *</Label>
                        <Select
                          value={bookingData.category}
                          onValueChange={(value) => setBookingData({ ...bookingData, category: value })}
                        >
                          <SelectTrigger className="mt-2 bg-zinc-800/50 border-zinc-700 text-white" data-testid="booking-category">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#18181B] border-zinc-800">
                            {CATEGORY_OPTIONS.map((opt) => {
                              const price = getTrainerPriceForCategory(profile, opt.value);
                              const disabled = !price || price <= 0;
                              return (
                                <SelectItem
                                  key={opt.value}
                                  value={opt.value}
                                  disabled={disabled}
                                  data-testid={`booking-category-option-${opt.value}`}
                                >
                                  {opt.label}{price ? `, ${formatCurrency(price)}` : ', Not offered'}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-zinc-300">Date *</Label>
                        <div className="mt-2 bg-zinc-800/50 border border-zinc-700 rounded-lg p-2">
                          <Calendar
                            mode="single"
                            selected={bookingData.scheduled_date}
                            onSelect={(date) => setBookingData({ ...bookingData, scheduled_date: date })}
                            disabled={(date) => date < new Date()}
                            className="mx-auto"
                            data-testid="booking-calendar"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-zinc-300">Time *</Label>
                        <Select
                          value={bookingData.scheduled_time}
                          onValueChange={(value) => setBookingData({ ...bookingData, scheduled_time: value })}
                        >
                          <SelectTrigger className="mt-2 bg-zinc-800/50 border-zinc-700 text-white" data-testid="booking-time">
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#18181B] border-zinc-800">
                            {timeSlots.map((time) => (
                              <SelectItem key={time} value={time}>{time}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-zinc-300">Your Address *</Label>
                        <Input
                          value={bookingData.location_address}
                          onChange={(e) => setBookingData({ ...bookingData, location_address: e.target.value })}
                          placeholder="Enter your full address"
                          className="mt-2 bg-zinc-800/50 border-zinc-700 text-white"
                          data-testid="booking-address"
                        />
                      </div>

                      <div>
                        <Label className="text-zinc-300">Notes (optional)</Label>
                        <Textarea
                          value={bookingData.notes}
                          onChange={(e) => setBookingData({ ...bookingData, notes: e.target.value })}
                          placeholder="Any special requests or information..."
                          className="mt-2 bg-zinc-800/50 border-zinc-700 text-white"
                          data-testid="booking-notes"
                        />
                      </div>

                      {bookingData.category && selectedPrice && (
                        <div className="p-4 bg-[#C0A062]/10 border border-[#C0A062]/20 rounded-lg">
                          <div className="flex items-center justify-between">
                            <span className="text-zinc-300">Session Price</span>
                            <span className="text-[#C0A062] font-bold text-xl">
                              {formatCurrency(selectedPrice)}
                            </span>
                          </div>
                        </div>
                      )}

                      <Button
                        onClick={handleBookSession}
                        disabled={bookingLoading}
                        className="w-full btn-primary py-6"
                        data-testid="confirm-booking-btn"
                      >
                        {bookingLoading ? (
                          <span className="spinner w-5 h-5" />
                        ) : (
                          <>
                            <DollarSign className="w-5 h-5 mr-2" />
                            Proceed to Payment
                          </>
                        )}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              ) : (
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full btn-primary py-6"
                  data-testid="login-to-book-btn"
                >
                  Sign In to Book
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <LiabilityWaiverModal
        open={waiverOpen}
        onAccept={() => {
          setWaiverOpen(false);
          proceedToCheckout();
        }}
        onClose={() => setWaiverOpen(false)}
      />
    </div>
  );
};

export default TrainerProfile;
